/**
 * VictoryScene
 *
 * Phase 5: Victory overlay displaying completion statistics
 */

import Phaser from 'phaser';
import { GAME_CONSTANTS } from '../config/constants';
import { ICONS } from '../config/icons';
import { GameStats } from '../systems/VictoryManager';
import { VictoryHistoryManager } from '../systems/VictoryHistoryManager';
import { BestHeightManager } from '../systems/BestHeightManager';
import { UsernameManager } from '../systems/UsernameManager';
import { SaveStateManager } from '../systems/SaveStateManager';
import { LeaderboardManager } from '../systems/LeaderboardManager';
import { SettingsManager } from '../systems/SettingsManager';
import { GlassButton } from '../ui/components/GlassButton';
import { Z_INDEX } from '../config/designSystem';

interface VictoryData {
  completionTime: number;
  stats: GameStats;
}

export class VictoryScene extends Phaser.Scene {
  private completionTime: number = 0;
  private previousBest: number = Infinity;
  private stats: GameStats = {
    completionTime: 0,
    totalBounces: 0,
    totalFalls: 0,
    highestJump: 0,
    attempts: 0,
    collectibles: 0,
    totalCollectibles: 45,
    devMode: false,
  };
  private isTransitioning: boolean = false;
  private globalRank: number | null = null;

  constructor() {
    super({ key: 'VictoryScene' });
  }

  init(data: VictoryData): void {
    this.completionTime = data.completionTime;
    this.stats = data.stats;
    this.previousBest = this.loadBestTime();
  }

  async create(): Promise<void> {
    const { width, height } = this.cameras.main;

    // Reset transition flag
    this.isTransitioning = false;

    // Semi-transparent background overlay
    this.createBackground(width, height);

    // Victory UI content
    this.displayTitle(width, height);
    this.displayTime(width, height);
    this.displayMedal(width, height);
    this.displayStats(width, height);
    this.displayPersonalBest(width, height);
    this.createButtons(width, height);

    // Save new best time if applicable
    const medalType = this.getMedalType();
    if (this.completionTime < this.previousBest) {
      this.saveBestTime(this.completionTime);
      this.showNewRecordBanner(width, height);
    }

    // Check if dev mode was used (teleport)
    if (this.stats.devMode) {
      // Show dev mode warning - don't save to leaderboard
      this.showDevModeWarning(width, height);
      return;
    }

    // Get username (auto-generates if first time)
    const username = UsernameManager.getUsername();

    // Get collectibles count from stats
    const collectibles = this.stats.collectibles || 0;
    const totalCollectibles = this.stats.totalCollectibles || 45;

    // Save this run to victory history (only if NOT dev mode)
    VictoryHistoryManager.saveRun(username, this.completionTime, medalType, collectibles);

    // Submit to global leaderboard ONLY if this is a new personal best
    const isNewBest = this.completionTime < this.previousBest;
    if (isNewBest) {
      await this.submitToGlobalLeaderboard(
        username,
        medalType,
        collectibles,
        totalCollectibles,
        width,
        height
      );
    } else {
      // Even if not a new best, fetch and display current rank
      await this.fetchAndDisplayCurrentRank(username, width, height);
    }

    // Remove best height record since player has now completed the level
    BestHeightManager.removeRecord(username);
  }

  private createBackground(width: number, height: number): void {
    const bg = this.add.graphics();
    bg.fillStyle(0x000000, 0.8);
    bg.fillRect(0, 0, width, height);
    bg.setScrollFactor(0);
  }

  private displayTitle(width: number, height: number): void {
    // Responsive font sizes based on screen width
    const isMobile = width < 600;
    const titleFontSize = isMobile ? '36px' : '48px';
    const subtitleFontSize = isMobile ? '18px' : '24px';
    const titleY = isMobile ? height * 0.08 : height * 0.15;
    const subtitleY = isMobile ? height * 0.15 : height * 0.22;

    const title = this.add.text(width / 2, titleY, 'VICTORY!', {
      fontFamily: GAME_CONSTANTS.UI.FONT_FAMILY,
      fontSize: titleFontSize,
      color: '#FFD700',
      fontStyle: 'bold',
      stroke: '#000000',
      strokeThickness: 6,
      resolution: 2, // Ultra high DPI: 2x multiplier
    });
    title.setOrigin(0.5);
    title.setScrollFactor(0);

    // Trophy icons on both sides of title
    const iconSize = isMobile ? 32 : 40;
    const spacing = 20;
    const leftIconX = width / 2 - title.width / 2 - iconSize / 2 - spacing;
    const rightIconX = width / 2 + title.width / 2 + iconSize / 2 + spacing;

    const leftTrophy = this.add.image(leftIconX, titleY, ICONS.TROPHY);
    leftTrophy.setDisplaySize(iconSize, iconSize);
    leftTrophy.setTint(0xffd700);
    leftTrophy.setScrollFactor(0);

    const rightTrophy = this.add.image(rightIconX, titleY, ICONS.TROPHY);
    rightTrophy.setDisplaySize(iconSize, iconSize);
    rightTrophy.setTint(0xffd700);
    rightTrophy.setScrollFactor(0);

    const subtitle = this.add.text(width / 2, subtitleY, 'YOU REACHED THE SUMMIT!', {
      fontFamily: GAME_CONSTANTS.UI.FONT_FAMILY,
      fontSize: subtitleFontSize,
      color: GAME_CONSTANTS.UI.COLOR_PRIMARY,
      fontStyle: 'bold',
      resolution: 2, // Ultra high DPI: 2x multiplier
    });
    subtitle.setOrigin(0.5);
    subtitle.setScrollFactor(0);
  }

  private displayTime(width: number, height: number): void {
    const isMobile = width < 600;
    const labelFontSize = isMobile ? 16 : 20;
    const valueFontSize = isMobile ? '28px' : '32px';
    const labelY = isMobile ? height * 0.22 : height * 0.32;
    const valueY = isMobile ? height * 0.28 : height * 0.38;

    // Completion Time label with timer icon
    const labelText = this.add.text(width / 2, labelY, 'Completion Time', {
      fontFamily: GAME_CONSTANTS.UI.FONT_FAMILY,
      fontSize: `${labelFontSize}px`,
      color: GAME_CONSTANTS.UI.COLOR_SECONDARY,
      resolution: 2, // Ultra high DPI: 2x multiplier
    });
    labelText.setOrigin(0.5).setScrollFactor(0);

    // Timer icon to the left of label
    const iconSize = labelFontSize;
    const iconX = width / 2 - labelText.width / 2 - iconSize / 2 - 8;
    const timerIcon = this.add.image(iconX, labelY, ICONS.TIMER);
    timerIcon.setDisplaySize(iconSize, iconSize);
    timerIcon.setTint(0x9ca3af); // Match secondary color
    timerIcon.setScrollFactor(0);

    const timeText = this.formatTime(this.completionTime);
    this.add
      .text(width / 2, valueY, timeText, {
        fontFamily: GAME_CONSTANTS.UI.FONT_FAMILY,
        fontSize: valueFontSize,
        color: GAME_CONSTANTS.UI.COLOR_ACCENT,
        fontStyle: 'bold',
        resolution: 2, // Ultra high DPI: 2x multiplier
      })
      .setOrigin(0.5)
      .setScrollFactor(0);
  }

  private displayMedal(width: number, height: number): void {
    const isMobile = width < 600;
    const fontSize = isMobile ? 18 : 24;
    const medalY = isMobile ? height * 0.36 : height * 0.48;

    const medalType = this.getMedalType();
    const medalText =
      medalType === 'gold'
        ? 'Gold Medal! (<5 min)'
        : medalType === 'silver'
          ? 'Silver Medal! (5-8 min)'
          : 'Bronze Medal! (8-15 min)';

    // Medal text
    const text = this.add.text(width / 2, medalY, medalText, {
      fontFamily: GAME_CONSTANTS.UI.FONT_FAMILY,
      fontSize: `${fontSize}px`,
      color: GAME_CONSTANTS.UI.COLOR_PRIMARY,
      fontStyle: 'bold',
      resolution: 2, // Ultra high DPI: 2x multiplier
    });
    text.setOrigin(0.5).setScrollFactor(0);

    // Medal icon to the left (using colored SVG)
    const medalIcons: Record<string, string> = {
      gold: ICONS.MEDAL_GOLD,
      silver: ICONS.MEDAL_SILVER,
      bronze: ICONS.MEDAL_BRONZE,
    };
    const iconSize = fontSize;
    const iconX = width / 2 - text.width / 2 - iconSize / 2 - 8;
    const medalIconKey = medalIcons[medalType] || ICONS.MEDAL_GOLD;
    const medalIcon = this.add.image(iconX, medalY, medalIconKey);
    medalIcon.setDisplaySize(iconSize, iconSize);
    medalIcon.setOrigin(0.5);
    medalIcon.setScrollFactor(0);
  }

  private displayStats(width: number, height: number): void {
    const isMobile = width < 600;
    const headerFontSize = isMobile ? 16 : 20;
    const statFontSize = isMobile ? '14px' : '18px';
    const statsY = isMobile ? height * 0.42 : height * 0.58;
    const lineSpacing = isMobile ? 18 : 25;
    const headerSpacing = isMobile ? 22 : 30;

    // Statistics header
    const headerText = this.add.text(width / 2, statsY, 'Statistics:', {
      fontFamily: GAME_CONSTANTS.UI.FONT_FAMILY,
      fontSize: `${headerFontSize}px`,
      color: GAME_CONSTANTS.UI.COLOR_SECONDARY,
      fontStyle: 'bold',
      resolution: 2, // Ultra high DPI: 2x multiplier
    });
    headerText.setOrigin(0.5).setScrollFactor(0);

    // Statistics icon to the left
    const iconSize = headerFontSize;
    const iconX = width / 2 - headerText.width / 2 - iconSize / 2 - 8;
    const statsIcon = this.add.image(iconX, statsY, ICONS.STATISTICS);
    statsIcon.setDisplaySize(iconSize, iconSize);
    statsIcon.setTint(0x9ca3af); // Match secondary color
    statsIcon.setScrollFactor(0);

    // NOTE: totalBounces and totalFalls are currently placeholders (always 0)
    // TODO: Implement tracking for:
    //   - totalBounces: Increment on BOUNCY platform collisions
    //   - totalFalls: Increment when fall distance exceeds threshold
    const statsText = [
      `• Total Bounces: ${this.stats.totalBounces}`,
      `• Total Falls: ${this.stats.totalFalls}`,
      `• Highest Jump: ${this.stats.highestJump.toFixed(1)}m`,
      `• Attempts: ${this.stats.attempts}`,
    ];

    statsText.forEach((text, index) => {
      this.add
        .text(width / 2, statsY + headerSpacing + index * lineSpacing, text, {
          fontFamily: GAME_CONSTANTS.UI.FONT_FAMILY,
          fontSize: statFontSize,
          color: GAME_CONSTANTS.UI.COLOR_PRIMARY,
          resolution: 2, // Ultra high DPI: 2x multiplier
        })
        .setOrigin(0.5)
        .setScrollFactor(0);
    });
  }

  private displayPersonalBest(width: number, height: number): void {
    const isMobile = width < 600;
    const fontSize = isMobile ? '14px' : '18px';
    const bestY = isMobile ? height * 0.6 : height * 0.75;

    if (this.previousBest !== Infinity) {
      this.add
        .text(width / 2, bestY, `${ICONS.TARGET}  Personal Best: ${this.formatTime(this.previousBest)}`, {
          fontFamily: GAME_CONSTANTS.UI.FONT_FAMILY,
          fontSize: fontSize,
          color: GAME_CONSTANTS.UI.COLOR_SECONDARY,
          resolution: 2, // Ultra high DPI: 2x multiplier
        })
        .setOrigin(0.5)
        .setScrollFactor(0);
    }
  }

  private showNewRecordBanner(width: number, height: number): void {
    const isMobile = width < 600;
    const fontSize = isMobile ? 18 : 24;
    const bannerY = isMobile ? height * 0.65 : height * 0.8;

    // NEW RECORD text
    const banner = this.add.text(width / 2, bannerY, 'NEW RECORD!', {
      fontFamily: GAME_CONSTANTS.UI.FONT_FAMILY,
      fontSize: `${fontSize}px`,
      color: '#FFD700',
      fontStyle: 'bold',
      stroke: '#000000',
      strokeThickness: 4,
      resolution: 2, // Ultra high DPI: 2x multiplier
    });
    banner.setOrigin(0.5);
    banner.setScrollFactor(0);

    // Star icons on both sides
    const iconSize = fontSize;
    const spacing = 15;
    const leftIconX = width / 2 - banner.width / 2 - iconSize / 2 - spacing;
    const rightIconX = width / 2 + banner.width / 2 + iconSize / 2 + spacing;

    const leftStar = this.add.image(leftIconX, bannerY, ICONS.STAR);
    leftStar.setDisplaySize(iconSize, iconSize);
    leftStar.setTint(0xffd700); // Gold
    leftStar.setScrollFactor(0);

    const rightStar = this.add.image(rightIconX, bannerY, ICONS.STAR);
    rightStar.setDisplaySize(iconSize, iconSize);
    rightStar.setTint(0xffd700); // Gold
    rightStar.setScrollFactor(0);

    // Pulsing animation
    this.tweens.add({
      targets: banner,
      scale: { from: 1.0, to: 1.2 },
      duration: 500,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });
  }

  private showDevModeWarning(width: number, height: number): void {
    // Replace victory title with dev mode warning
    this.add
      .text(width / 2, height * 0.3, '⚠️ DEV MODE ⚠️', {
        fontFamily: GAME_CONSTANTS.UI.FONT_FAMILY,
        fontSize: '32px',
        color: '#FF6B6B',
        fontStyle: 'bold',
        stroke: '#000000',
        strokeThickness: 4,
        resolution: 2,
      })
      .setOrigin(0.5)
      .setScrollFactor(0);

    this.add
      .text(width / 2, height * 0.38, 'Teleport was used', {
        fontFamily: GAME_CONSTANTS.UI.FONT_FAMILY,
        fontSize: '20px',
        color: GAME_CONSTANTS.UI.COLOR_SECONDARY,
        resolution: 2,
      })
      .setOrigin(0.5)
      .setScrollFactor(0);

    this.add
      .text(width / 2, height * 0.45, 'This run will not be saved to the leaderboard', {
        fontFamily: GAME_CONSTANTS.UI.FONT_FAMILY,
        fontSize: '18px',
        color: GAME_CONSTANTS.UI.COLOR_PRIMARY,
        resolution: 2,
      })
      .setOrigin(0.5)
      .setScrollFactor(0);
  }

  private createButtons(width: number, height: number): void {
    const isMobile = width < 600;

    if (isMobile) {
      // Stack buttons vertically on mobile to prevent overlap
      const button1Y = height * 0.78;
      const button2Y = height * 0.87;
      const buttonWidth = Math.min(280, width * 0.75);

      // Play Again button (stacked on top, primary style)
      const playAgainButton = new GlassButton(this, width / 2, button1Y, {
        text: 'PLAY AGAIN',
        iconKey: ICONS.PLAY_CIRCLE,
        fontSize: 20,
        width: buttonWidth,
        height: 50,
        style: 'primary',
        onClick: () => {
          if (!this.isTransitioning) {
            this.playAgain();
          }
        },
      });
      playAgainButton.setDepth(Z_INDEX.uiContent);

      // Main Menu button (stacked below, secondary style)
      const menuButton = new GlassButton(this, width / 2, button2Y, {
        text: 'MAIN MENU',
        iconKey: ICONS.ARROW_BACK,
        fontSize: 20,
        width: buttonWidth,
        height: 50,
        style: 'secondary',
        onClick: () => {
          if (!this.isTransitioning) {
            this.returnToMenu();
          }
        },
      });
      menuButton.setDepth(Z_INDEX.uiContent);
    } else {
      // Side by side on desktop
      const buttonY = height * 0.86;

      // Play Again button (centered left, primary style)
      const playAgainButton = new GlassButton(this, width * 0.35, buttonY, {
        text: 'PLAY AGAIN',
        iconKey: ICONS.PLAY_CIRCLE,
        fontSize: 22,
        width: 240,
        height: 60,
        style: 'primary',
        onClick: () => {
          if (!this.isTransitioning) {
            this.playAgain();
          }
        },
      });
      playAgainButton.setDepth(Z_INDEX.uiContent);

      // Main Menu button (centered right, secondary style)
      const menuButton = new GlassButton(this, width * 0.65, buttonY, {
        text: 'MAIN MENU',
        iconKey: ICONS.ARROW_BACK,
        fontSize: 22,
        width: 240,
        height: 60,
        style: 'secondary',
        onClick: () => {
          if (!this.isTransitioning) {
            this.returnToMenu();
          }
        },
      });
      menuButton.setDepth(Z_INDEX.uiContent);
    }
  }

  private playAgain(): void {
    // Set transition flag to prevent double-tap
    this.isTransitioning = true;

    // Set flag to prevent GameScene from auto-saving on shutdown
    this.registry.set('skipAutoSave', true);

    // Stop UIScene and GameScene (shutdown will check skipAutoSave flag)
    this.scene.stop('UIScene');
    this.scene.stop('GameScene');

    // Clear saved state to start fresh from ground level
    SaveStateManager.clearState();

    // Don't clear flag here - GameScene.create() will clear it
    // This ensures flag is still true when shutdown event fires

    // Use scene.start to start GameScene fresh from ground level
    this.scene.start('GameScene');
  }

  private returnToMenu(): void {
    // Set transition flag to prevent double-tap
    this.isTransitioning = true;

    // Set flag to prevent GameScene from auto-saving on shutdown
    this.registry.set('skipAutoSave', true);

    // Stop all game scenes (shutdown will check skipAutoSave flag)
    this.scene.stop('UIScene');
    this.scene.stop('GameScene');

    // Clear saved state (level completed, no need to resume)
    SaveStateManager.clearState();

    // Don't clear flag here - next scene will clear it
    // This ensures flag is still true when shutdown event fires

    // Start MainMenuScene (which will automatically stop VictoryScene)
    this.scene.start('MainMenuScene');
  }

  /**
   * Submit to global leaderboard (only called for new personal bests)
   */
  private async submitToGlobalLeaderboard(
    username: string,
    medal: 'gold' | 'silver' | 'bronze',
    collectibles: number,
    totalCollectibles: number,
    width: number,
    height: number
  ): Promise<void> {
    // Check if leaderboard submission is disabled (developer setting)
    if (SettingsManager.getDisableLeaderboardSubmission()) {
      console.log('🚫 Leaderboard submission disabled (developer setting)');
      return;
    }

    try {
      console.log('📤 Submitting new personal best to leaderboard...');

      const result = await LeaderboardManager.submitRun({
        username,
        completionTime: this.completionTime,
        medal,
        collectibles,
        totalCollectibles,
      });

      if (result.success && result.rank) {
        this.globalRank = result.rank;
        this.displayGlobalRank(width, height);
        console.log(`✅ Global rank: #${result.rank}`);
      } else {
        console.warn('⚠️ Failed to submit to global leaderboard:', result.error);
      }
    } catch (error) {
      console.error('❌ Error submitting to global leaderboard:', error);
      // Gracefully fail - don't show error to user
    }
  }

  /**
   * Fetch and display current rank (for runs that aren't new personal bests)
   */
  private async fetchAndDisplayCurrentRank(
    username: string,
    width: number,
    height: number
  ): Promise<void> {
    try {
      console.log('📊 Fetching current leaderboard rank...');
      const rank = await LeaderboardManager.getUserRank(username);

      if (rank !== null) {
        this.globalRank = rank;
        this.displayGlobalRank(width, height);
        console.log(`✅ Current rank: #${rank}`);
      } else {
        console.log('ℹ️ User not yet ranked in leaderboard');
      }
    } catch (error) {
      console.error('❌ Error fetching current rank:', error);

      // Show offline message if disconnected
      if (
        error instanceof Error &&
        (error.message === 'OFFLINE' || error.name === 'AbortError' || !navigator.onLine)
      ) {
        const offlineText = this.add.text(
          width / 2,
          height * 0.78,
          `${ICONS.WIFI_OFF}  Offline - Can't check rank`,
          {
            fontFamily: GAME_CONSTANTS.UI.FONT_FAMILY,
            fontSize: '16px',
            color: '#FFA500',
            fontStyle: 'bold',
            resolution: 2,
          }
        );
        offlineText.setOrigin(0.5);
        offlineText.setScrollFactor(0);
      }
    }
  }

  /**
   * Display leaderboard rank badge (called after successful submission)
   */
  private displayGlobalRank(width: number, height: number): void {
    if (!this.globalRank) return;

    const isMobile = width < 600;
    const fontSize = isMobile ? '16px' : '20px';
    const rankY = isMobile ? height * 0.7 : height * 0.82;

    const rankText = this.add.text(width / 2, rankY, `${ICONS.TROPHY}  Leaderboard Rank: #${this.globalRank}`, {
      fontFamily: GAME_CONSTANTS.UI.FONT_FAMILY,
      fontSize: fontSize,
      color: '#4ECDC4',
      fontStyle: 'bold',
      stroke: '#000000',
      strokeThickness: 3,
      resolution: 2,
    });
    rankText.setOrigin(0.5);
    rankText.setScrollFactor(0);
    rankText.setDepth(Z_INDEX.uiContent);

    // Pulse animation
    this.tweens.add({
      targets: rankText,
      scale: { from: 0.9, to: 1.1 },
      duration: 800,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });
  }

  private formatTime(milliseconds: number): string {
    const minutes = Math.floor(milliseconds / 60000);
    const seconds = Math.floor((milliseconds % 60000) / 1000);
    const ms = Math.floor((milliseconds % 1000) / 100);
    return `${minutes}:${seconds.toString().padStart(2, '0')}.${ms}`;
  }

  private getMedalType(): 'gold' | 'silver' | 'bronze' {
    if (this.completionTime < GAME_CONSTANTS.VICTORY.MEDAL_THRESHOLDS.GOLD) {
      return 'gold';
    } else if (this.completionTime < GAME_CONSTANTS.VICTORY.MEDAL_THRESHOLDS.SILVER) {
      return 'silver';
    } else {
      return 'bronze';
    }
  }

  private loadBestTime(): number {
    const savedBest = localStorage.getItem('blobo-best-time');
    return savedBest ? parseFloat(savedBest) : Infinity;
  }

  private saveBestTime(time: number): void {
    localStorage.setItem('blobo-best-time', time.toString());
  }
}
